import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { adminRequest, operationKey } from "lib/adminSiteApi";
export const label = (key) =>
  ({
    id: "Record ID",
    assignedTo: "Assigned staff member",
    versionId: "Download file version",
    previewVersionId: "PDF preview version",
    expiresAt: "Expires at",
    resourceId: "Resource",
    customerId: "Customer",
    seoTitle: "SEO title",
    metaDescription: "Search description",
    faqs: "Frequently asked questions",
    usBusinessHours: "US business hours",
    mcNumber: "MC number",
    dotNumber: "DOT number",
  })[key] ||
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/^./, (s) => s.toUpperCase());
export function ErrorNotice({ error }) {
  return error ? (
    <div className="aw-notice aw-error" role="alert">
      <strong>Action needs attention</strong>
      <p>{error.message}</p>
      {error.requestId && <small>Reference: {error.requestId}</small>}
    </div>
  ) : null;
}
// keep: on a reload of the same path, keep showing the current data until the new data arrives
// (lists beside an open card must not blank out and jump when the card saves).
export function useRemote(path, refresh = 0, { keep = false, timeoutMs = 20000 } = {}) {
  const [state, setState] = useState({
    loading: true,
    data: null,
    error: null,
  });
  const loaded = useRef(null);
  useEffect(() => {
    if (!path) {
      loaded.current = null;
      setState({ loading: false, data: null, error: null });
      return;
    }
    const ctrl = new AbortController();
    const same = keep && loaded.current === path;
    loaded.current = path;
    const timer = setTimeout(() => {
      ctrl.abort();
      setState(prev => ({ path, loading: false, data: same ? prev.data : null, error: new Error("Reading the current record timed out. Reload before editing or saving.") }));
    }, timeoutMs);
    setState((prev) => (same && prev.data ? { ...prev, loading: true, error: null } : { path, loading: true, data: null, error: null }));
    adminRequest(path, { signal: ctrl.signal })
      .then((r) => {
        if (!ctrl.signal.aborted)
          setState({ path, loading: false, data: r.data, error: null });
      })
      .catch((error) => {
        if (!ctrl.signal.aborted)
          setState(prev => ({ path, loading: false, data: same ? prev.data : null, error }));
      }).finally(() => clearTimeout(timer));
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [path, refresh, keep, timeoutMs]);
  return path && state.path !== path ? { loading: true, data: null, error: null } : state;
}
export function Loading({ state, children }) {
  if (state.loading && !state.data)
    return (
      <p role="status" className="aw-notice">
        Loading records…
      </p>
    );
  if (state.error && !state.data) return <ErrorNotice error={state.error} />;
  return <>{state.error && <ErrorNotice error={state.error} />}{children}</>;
}
const privateField =
  /^(token|tokenHash|challengeHash|sessionEpoch|password|secret|authorization|cookie|lease|objectPath|generation|parts)$/i;
export function Record({ data }) {
  if (data == null) return <span className="aw-muted">Not supplied</span>;
  if (typeof data !== "object")
    return (
      <span className="aw-value">
        {typeof data === "boolean" ? (data ? "Yes" : "No") : String(data)}
      </span>
    );
  if (data._seconds !== undefined)
    return <time>{new Date(data._seconds * 1000).toLocaleString()}</time>;
  if (Array.isArray(data))
    return data.length ? (
      <ol className="aw-record-list">
        {data.map((v, i) => (
          <li key={i}>
            <Record data={v} />
          </li>
        ))}
      </ol>
    ) : (
      <span className="aw-muted">None recorded</span>
    );
  return (
    <dl className="aw-record">
      {Object.entries(data)
        .filter(([k]) => !privateField.test(k))
        .map(([k, v]) => (
          <div key={k}>
            <dt>{label(k)}</dt>
            <dd>
              {/At$/.test(k) && typeof v === "number" ? (
                new Date(v).toLocaleString()
              ) : (
                <Record data={v} />
              )}
            </dd>
          </div>
        ))}
    </dl>
  );
}
export function Badge({ value, text }) {
  return (
    <span className={`aw-badge aw-${String(value).replace(/[^a-z_]/g, "")}`}>
      {text ?? label(String(value ?? "not recorded"))}
    </span>
  );
}
export function fieldSchema(schema, value) {
  if (schema.anyOf && value !== undefined) {
    const type = Array.isArray(value) ? "array" : value === null ? "null" : typeof value;
    const match = schema.anyOf.find(s => s.type === type || (type === "number" && s.type === "integer"));
    if (match) return match;
  }
  if (schema.anyOf)
    return (
      schema.anyOf.find((s) => s.type === "object") ||
      schema.anyOf.find((s) => s.type) ||
      schema.anyOf[0]
    );
  return schema;
}
export function cleanDraft(schema, input) {
  schema = fieldSchema(schema, input);
  if (schema.type === "object")
    return Object.fromEntries(
      Object.entries(schema.properties || {})
        .filter(
          ([k, s]) =>
            input?.[k] !== undefined &&
            !(
              input[k] === "" &&
              !schema.required?.includes(k) &&
              s.pattern &&
              !new RegExp(s.pattern).test("")
            ),
        )
        .map(([k, s]) => [k, cleanDraft(s, input[k])]),
    );
  if (schema.type === "array")
    return (input || []).map((v) => cleanDraft(schema.items, v));
  return input;
}
export function Fields({
  schema,
  value = {},
  onChange,
  choices = {},
  prefix = "",
  fieldCopy = {},
  reorder = false,
}) {
  return (
    <div className="aw-fields">
      {Object.entries(schema.properties || {})
        .filter(([name]) => name !== "revision")
        .map(([name, s]) => (
          <Field
            key={name}
            name={name}
            schema={s}
            copy={fieldCopy[name]}
            value={value[name]}
            required={schema.required?.includes(name)}
            onChange={(v) => onChange({ ...value, [name]: v })}
            choices={choices[name]}
            prefix={prefix}
            reorder={reorder}
          />
        ))}
    </div>
  );
}
function Field({ name, schema, value, onChange, required, choices, prefix, reorder, copy }) {
  schema = fieldSchema(schema);
  const title = copy?.label || label(name),
    id = `aw-${prefix}-${name}`;
  if (schema.const !== undefined)
    return (
      <div>
        <p>
          {title}: {String(schema.const)}
        </p>
      </div>
    );
  if (schema.type === "array") {
    const values = value || [],
      item = fieldSchema(schema.items),
      object = item.type === "object";
    return (
      <fieldset className="aw-array">
        <legend>{title}</legend>
        {values.map((row, i) => (
          <div className="aw-array-row" key={i}>
            {object ? (
              <Fields
                schema={item}
                value={
                  Array.isArray(row)
                    ? { question: row[0], answer: row[1] }
                    : typeof row === "string" ? { title: row } : row
                }
                onChange={(v) =>
                  onChange(values.map((x, j) => (j === i ? v : x)))
                }
                reorder={reorder}
                prefix={`${prefix}-${name}-${i}`}
              />
            ) : (
              <textarea
                aria-label={`${title} ${i + 1}`}
                rows={2}
                value={row}
                maxLength={item.maxLength}
                onChange={(e) =>
                  onChange(values.map((x, j) => (j === i ? e.target.value : x)))
                }
              />
            )}
            {reorder && <div className="aw-row">{[-1, 1].map(step => <button type="button" className="aw-button aw-small" key={step} disabled={i + step < 0 || i + step >= values.length} aria-label={`Move ${title.toLowerCase()} ${i + 1} ${step < 0 ? 'up' : 'down'}`} onClick={() => { const next = [...values]; [next[i], next[i + step]] = [next[i + step], next[i]]; onChange(next); }}>{step < 0 ? 'Move up' : 'Move down'}</button>)}</div>}
            <button
              type="button"
              className="aw-button aw-small"
              onClick={() => onChange(values.filter((_, j) => j !== i))}
            >
              Remove {title.toLowerCase()} {i + 1}
            </button>
          </div>
        ))}
        <button
          type="button"
          className="aw-button"
          disabled={values.length >= (schema.maxItems || 100)}
          onClick={() => onChange([...values, object ? Object.fromEntries(Object.entries(item.properties || {}).filter(([k, v]) => item.required?.includes(k) && v.type === "array").map(([k]) => [k, []])) : ""])}
        >
          Add {title.toLowerCase()}
        </button>
      </fieldset>
    );
  }
  if (schema.type === "object")
    return (
      <fieldset className="aw-array">
        <legend>{title}</legend>
        <Fields
          schema={schema}
          value={value || {}}
          onChange={onChange}
          reorder={reorder}
          prefix={`${prefix}-${name}`}
          fieldCopy={copy?.fields}
        />
      </fieldset>
    );
  if (schema.type === "boolean")
    return (
      <label className="aw-check" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={value ?? schema.default ?? false}
          onChange={(e) => onChange(e.target.checked)}
        />
        {title}
      </label>
    );
  const options =
    choices || schema.enum?.map((v) => ({ id: v, name: label(v) }));
  const long =
    !options &&
    (schema.maxLength >= 4000 ||
      /body|message|note|resolution|description|intro|answer|text/i.test(name));
  return (
    <div className={long ? "aw-wide" : ""}>
      <label htmlFor={id}>
        {title}
        {required ? " *" : ""}
      </label>
      {options ? (
        <select
          id={id}
          aria-describedby={copy?.help ? `${id}-help` : undefined}
          required={required}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{required ? "Choose…" : "Not selected"}</option>
          {value && !options.some((o) => String(o.id) === String(value)) && (
            <option value={value}>Current: {value}</option>
          )}
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name || o.title || o.id}
            </option>
          ))}
        </select>
      ) : long ? (
        <textarea
          id={id}
          aria-describedby={copy?.help ? `${id}-help` : undefined}
          rows={name === "body" ? 12 : 4}
          required={required}
          maxLength={schema.maxLength}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          id={id}
          aria-describedby={copy?.help ? `${id}-help` : undefined}
          required={required}
          type={
            schema.type === "integer"
              ? "number"
              : copy?.secret
                ? "password"
                : schema.format === "email"
                  ? "email"
                  : "text"
          }
          autoComplete={copy?.secret ? "new-password" : undefined}
          spellCheck={copy?.secret ? false : undefined}
          min={schema.minimum}
          max={schema.maximum}
          maxLength={schema.maxLength}
          minLength={schema.minLength}
          pattern={schema.pattern}
          value={value ?? ""}
          onChange={(e) =>
            onChange(
              schema.type === "integer"
                ? e.target.value === ""
                  ? undefined
                  : Number(e.target.value)
                : e.target.value,
            )
          }
        />
      )}
      {copy?.help && <p id={`${id}-help`} className="aw-muted">{copy.help}</p>}
    </div>
  );
}
export function ActionForm({
  title,
  schema,
  initial = {},
  choices = {},
  submit,
  onDone,
  button = "Save changes",
  description,
  children,
  additions,
  renderFields,
}) {
  const [value, setValue] = useState(initial),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(null),
    [receipt, setReceipt] = useState(null);
  const pending = useRef(null),
    alive = useRef(true);
  const baseline = useRef(initial);
  const [remoteChanged, setRemoteChanged] = useState(false);
  const initialKey = JSON.stringify(initial);
  useLayoutEffect(() => {
    const next = JSON.parse(initialKey);
    if (JSON.stringify(baseline.current) === initialKey) return;
    setValue(current => {
      if (JSON.stringify(cleanDraft(schema, current)) === JSON.stringify(cleanDraft(schema, baseline.current))) { baseline.current = next; setRemoteChanged(false); return next; }
      setRemoteChanged(true); return current;
    });
  }, [initialKey, schema]);
  const additionsKey = JSON.stringify(additions || {});
  useLayoutEffect(() => {
    setValue((v) => ({ ...v, ...JSON.parse(additionsKey) }));
  }, [additionsKey]);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const send = async (e) => {
    e.preventDefault();
    if (busy) return;
    const body = cleanDraft(schema, value),
      digest = JSON.stringify(body);
    if (pending.current?.digest !== digest)
      pending.current = { digest, key: operationKey() };
    setBusy(true);
    setError(null);
    setReceipt(null);
    try {
      const result = await submit(body, pending.current.key, baseline.current);
      pending.current = null;
      if (alive.current) {
        baseline.current = { ...value, revision: result.data?.revision ?? baseline.current.revision };
        setValue(v => ({ ...v, revision: baseline.current.revision }));
        setRemoteChanged(false);
        setReceipt(result);
        onDone?.(result);
      }
    } catch (err) {
      if (alive.current) setError(err);
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  return (
    <section className="aw-panel">
      <h2>{title}</h2>
      {description && <p className="aw-muted">{description}</p>}
      {remoteChanged && <div className="aw-notice" role="alert">Newer record data is available. Your unsaved edits are preserved. <button type="button" className="aw-button" onClick={() => { baseline.current = initial; setValue(initial); setRemoteChanged(false); }}>Reload and discard local edits</button></div>}
      <form onSubmit={send}>
        <fieldset disabled={busy}>
          {renderFields ? renderFields(value, setValue) : <Fields
            schema={schema}
            value={value}
            onChange={setValue}
            choices={choices}
            prefix={title.replace(/\W/g, "")}
          />}
          {typeof children === "function"
            ? children(value, setValue)
            : children}
          <ErrorNotice error={error} />
          {receipt && (
            <div className="aw-notice" role="status">
              Saved successfully.
              {receipt.data?.revision
                ? ` Revision ${receipt.data.revision}.`
                : ""}
              {receipt.requestId && <small>Receipt: {receipt.requestId}</small>}
            </div>
          )}
          <button
            className="aw-button aw-primary"
            type="submit"
            disabled={busy}
          >
            {busy ? "Saving…" : button}
          </button>
        </fieldset>
      </form>
    </section>
  );
}
export const text = (max = 1000) => ({ type: "string", maxLength: max });
export const requiredText = (max = 1000) => ({ ...text(max), minLength: 1 });
export const select = (...values) => ({ type: "string", enum: values });
export const formSchema = (properties, required = []) => ({
  type: "object",
  properties,
  required,
});
export const revision = { type: "integer", minimum: 0 };
